import { useState, useCallback, useEffect, useRef, memo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../../config/googleMaps";
import { getMapStyles } from "../../../components/map/mapStyles";
import {
  VEHICLE_STATUS_COLORS,
  VehicleMarkerGlyph,
  getMarkerShape,
} from "../../../components/map/vehicleMarkers";
import { STATUS_META, type StatusId } from "../../lite/components/Sidebar/statusMeta";
import {
  shipmentPosition,
  shipmentStage,
  type Shipment,
} from "../../../features/shipments/types";
import {
  EntityCard,
  EntityCardBody,
  EntityCardHeader,
  EntityCardRow,
} from "../../../components/ui";
import { useShipmentUnit } from "../hooks/useShipmentUnit";
import { STAGE_TONES } from "../statusTones";

interface KanbanCardProps {
  shipment: Shipment;
  onClick: () => void;
}

const MINI_MARKER_SIZE = 16;

const MiniMapMarker = memo(
  ({
    lat,
    lng,
    status,
    map,
  }: {
    lat: number;
    lng: number;
    status: StatusId;
    map: google.maps.Map | null;
  }) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const markerColor = VEHICLE_STATUS_COLORS[status] || VEHICLE_STATUS_COLORS.offline;
    const markerStyle = getMarkerShape(status, null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";

      class MiniOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, element: HTMLDivElement) {
          super();
          this.position = position;
          this.div = element;
        }

        onAdd() {
          const panes = this.getPanes();
          panes?.overlayMouseTarget.appendChild(this.div);
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;
          const pos = projection.fromLatLngToDivPixel(this.position);
          if (!pos) return;
          this.div.style.transform = "translate(-50%, -50%)";
          this.div.style.left = `${pos.x}px`;
          this.div.style.top = `${pos.y}px`;
        }

        onRemove() {
          this.div.parentNode?.removeChild(this.div);
        }
      }

      const overlay = new MiniOverlay(new google.maps.LatLng(lat, lng), div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        overlayRef.current?.setMap(null);
        overlayRef.current = null;
        setContainer(null);
      };
    }, [map, lat, lng]);

    if (!container) return null;

    return createPortal(
      <div style={{ width: `${MINI_MARKER_SIZE}px`, height: `${MINI_MARKER_SIZE}px` }}>
        <VehicleMarkerGlyph
          glyph={markerStyle.glyph}
          color={markerColor}
          size={MINI_MARKER_SIZE}
          rotation={markerStyle.rotation}
        />
      </div>,
      container,
    );
  },
);

export function KanbanCard({ shipment, onClick }: KanbanCardProps) {
  const { t } = useTranslation("envios");
  const unit = useShipmentUnit(shipment.actual_device_id);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const onMapLoad = useCallback((map: google.maps.Map) => setMapRef(map), []);

  const stage = shipmentStage(shipment);
  const history = shipmentPosition(shipment);
  const position =
    unit && unit.latitude !== null && unit.longitude !== null
      ? { lat: unit.latitude, lng: unit.longitude }
      : history
        ? { lat: history.lat, lng: history.long }
        : null;

  const route = [shipment.origin.address, shipment.destination.address]
    .filter(Boolean)
    .join(" → ");

  return (
    <EntityCard accent={STAGE_TONES[stage]} onClick={onClick} className="mb-3">
      <div className="h-36 w-full overflow-hidden rounded-t-2xl border-b border-line-soft">
        {isLoaded && position ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={position}
            zoom={12}
            onLoad={onMapLoad}
            options={{
              styles: getMapStyles(),
              disableDefaultUI: true,
              scrollwheel: false,
              gestureHandling: "none",
              disableDoubleClickZoom: true,
            }}
          >
            {mapRef && (
              <MiniMapMarker
                lat={position.lat}
                lng={position.lng}
                status={unit?.status ?? "offline"}
                map={mapRef}
              />
            )}
          </GoogleMap>
        ) : (
          <div className="w-full h-full bg-subtle flex flex-col items-center justify-center gap-1.5">
            <i className="ri-map-pin-line text-lg text-content-muted opacity-40" />
            <p className="text-[10px] text-content-muted">{t("detail.noPosition")}</p>
          </div>
        )}
      </div>

      <EntityCardHeader
        icon={
          unit ? (
            <span className={`h-2.5 w-2.5 rounded-full ${STATUS_META[unit.status].dot}`} />
          ) : (
            <i className="ri-truck-line" />
          )
        }
        iconTone={STAGE_TONES[stage]}
        title={shipment.client_name}
        subtitle={shipment.tracking_number}
      />

      <EntityCardBody>
        {route && (
          <EntityCardRow label={t("card.destination")} align="start">
            {route}
          </EntityCardRow>
        )}
        <EntityCardRow label={t("card.unit")}>
          {unit?.name ?? t("card.noUnit")}
        </EntityCardRow>
        <EntityCardRow label={t("card.driver")}>
          {unit?.driver || t("card.noDriver")}
        </EntityCardRow>
      </EntityCardBody>
    </EntityCard>
  );
}
